import {CSSProperties} from 'react';

import {
  buildSheetCoordinateCss,
  EmojiDefinition,
  EmojiSheetPosition,
} from '../../../src/services/emojis/emojis';
import {SkinTonesEnum} from '../../../src/services/emojis/emojiTypes';
import {emojiUrl} from '../../../src/services/emojis/emojiUrls';
import {shortcodeToUnified} from './emojiTextHelpers';

/*
 * Emoji transforms.
 */

/** Find the image URL for a given emoji + skin variation. */
export function emojiToUrl(emoji: EmojiDefinition, skinTone?: SkinTonesEnum) {
  const {variations, customUrl} = emoji;
  if (customUrl) {
    return customUrl;
  }

  const variation = skinTone && variations && variations[skinTone];

  // If we don't need a variation, build the default URL.
  if (!variation) {
    return `${emojiUrl}${emoji.id}.png`;
  }

  // Otherwise, build the skin variation URL.
  return `${emojiUrl}${variation.id}.png`;
}

/** Find the full shortcode for an emoji + skin variation. */
export function emojiToShortcode(emoji: EmojiDefinition, skinTone?: SkinTonesEnum) {
  const {variations} = emoji;
  const variation = skinTone && variations && variations[skinTone];

  // if we don't have a variation, return the emoji's shortcode.
  if (!variation) {
    return `:${emoji.shortcode}:`;
  }

  // Otherwise, return shortcode + variation.
  return `:${emoji.shortcode}::skin-tone-${skinTone}:`;
}

/** Find the unicode representation for an emoji + skin variation. */
export function emojiToUnified(emoji: EmojiDefinition, skinTone?: SkinTonesEnum) {
  return shortcodeToUnified(emojiToShortcode(emoji, skinTone));
}

/*
 * Sheet helpers.
 */

export function getEmojiSheetCss(emoji: EmojiDefinition, skinTone?: SkinTonesEnum): CSSProperties {
  // Find the position for the specified emoji.
  const position = getPosition(emoji, skinTone);

  // Return the corresponding CSS.
  return buildSheetCoordinateCss(position);
}

function getPosition(emoji: EmojiDefinition, skinTone?: SkinTonesEnum): EmojiSheetPosition {
  const {variations} = emoji;
  const variation = skinTone && variations && variations[skinTone];

  // If we don't need a variation, return the main coordinates.
  if (!variation) {
    return {
      sheetX: emoji.sheetX,
      sheetY: emoji.sheetY,
    };
  }

  // Otherwise, return the variation coordinates.
  return {
    sheetX: variation.sheetX,
    sheetY: variation.sheetY,
  };
}
